import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Node as ReactFlowNode, Edge as ReactFlowEdge } from 'reactflow';
import { UrlFilters } from '../../../hooks/useUrlFilters';
import { Report } from '../../../types/types';
import { isInSaaSMode } from '../../../utils/common';
import useAccount from '../../../hooks/useAccount';
import LineageDataWorker from '../worker/lineageDataWorker?worker&inline';
import {
  ApiLineage,
  DagDepthBoundary,
  EnrichedLineageNode,
  HopLimit,
  OnMessageData,
  PostMessageData,
  ReactFlowNodeData,
  StreamDirection,
} from '../types';
import {
  getReactFlowNodeParentIds,
  getReactFlowNodeChildrenRecursively,
} from '../utils/lineageGraphUtils';
import { isColumnNode } from '../utils/lineageApiParsing';
import {
  LINEAGE_PER_PARENT_COLUMNS_LIMIT,
  MAX_GRAPH_NODES_WARNING,
} from '../constants';
import { EnvMode } from '../../../constants';
import { useLineageGraphContext } from '../LineageGraphContext';
import useLineageApi from './useLineageApi';
import { useHighlightColumnLineage } from './useHighlightColumnLineage';
import { useFetchAssetColumns } from './useAssetColumns';

let lineageDataWorker: Worker | null;

type Props = {
  reportData?: Report;
  graphNodes?: ReactFlowNode<ReactFlowNodeData, string | undefined>[];
  assetIds?: string[];
  columnIds?: string[];
  filters?: UrlFilters;
  streamDirection?: StreamDirection;
  hopLimit?: HopLimit;
  calcDownstreamUpstream?: boolean;
  forceLoadLargeGraph?: boolean;
  /** Pre-fetched lineage payload used instead of the GraphQL fetch; still requires `assetIds`/`columnIds` for root traversal. */
  overrideLineageData?: ApiLineage | null;
};

const hasLoadedColumnChildren = (node: ReactFlowNode<ReactFlowNodeData>) =>
  !!node.data.children?.some((child) => isColumnNode(child.data.metadata));

const withoutValue = <T>(set: Set<T>, value: T): Set<T> => {
  if (!set.has(value)) return set;
  const next = new Set(set);
  next.delete(value);
  return next;
};

const useLineageData = ({
  reportData,
  graphNodes,
  assetIds,
  columnIds,
  filters,
  streamDirection,
  hopLimit,
  calcDownstreamUpstream,
  forceLoadLargeGraph,
  overrideLineageData,
}: Props) => {
  const [rootNodes, setRootNodes] = useState<EnrichedLineageNode[]>([]);
  const [nodes, setNodes] = useState<ReactFlowNode<ReactFlowNodeData>[]>([]);
  const [edges, setEdges] = useState<ReactFlowEdge[]>([]);
  const [loading, setLoading] = useState(true);
  const [workerError, setWorkerError] = useState<{
    message: string;
    stack?: string;
  } | null>(null);
  const [columnsLoadingAssetIds, setColumnsLoadingAssetIds] = useState<
    Set<string>
  >(new Set());

  const { highlightedNode, setHighlightedNode } = useLineageGraphContext();
  const { fetchAssetColumns } = useFetchAssetColumns();
  const pendingHighlightSubgraphRef = useRef<string | undefined>();

  const [upstreamNodes, setUpstreamNodes] = useState<
    EnrichedLineageNode[] | undefined
  >();
  const [downstreamNodes, setDownstreamNodes] = useState<
    EnrichedLineageNode[] | undefined
  >();
  const [dagDepthBoundary, setDagDepthBoundary] = useState<DagDepthBoundary>();
  const [hitGraphLimits, setHitGraphLimits] = useState<boolean>(false);

  const selectedNodes = useMemo(
    () => [...(assetIds ?? []), ...(columnIds ?? [])],
    [assetIds, columnIds],
  );

  const selectionTooLarge =
    (assetIds?.length ?? 0) >= MAX_GRAPH_NODES_WARNING && !forceLoadLargeGraph;

  const { data: account } = useAccount();

  const isApiEnabled = !!account?.use_assets_api;
  const hasColumnFilter = !!columnIds?.length;

  const {
    apiLineage: queryApiLineage,
    isLineageFetching,
    apiDagDepthBoundary,
    exceededLimitParentAssets,
  } = useLineageApi({
    enabled: isApiEnabled && overrideLineageData == null,
    assetIds,
    columnIds,
    hopLimit,
    perParentColumnsLimit: LINEAGE_PER_PARENT_COLUMNS_LIMIT,
    selectionTooLarge,
  });

  const apiLineage = overrideLineageData ?? queryApiLineage;

  const { highlightColumnSubgraph, isHighlightLineageFetching } =
    useHighlightColumnLineage({
      enabled: isApiEnabled && overrideLineageData == null,
      assetIds,
      highlightedColumnId:
        highlightedNode?.parentId && isColumnNode(highlightedNode.metadata)
          ? highlightedNode.id
          : undefined,
      hopLimit,
      perParentColumnsLimit: LINEAGE_PER_PARENT_COLUMNS_LIMIT,
      hasColumnFilter,
    });

  const excludeColumnNodes = isApiEnabled
    ? !hasColumnFilter && overrideLineageData == null
    : import.meta.env.MODE !== EnvMode.Development && !isInSaaSMode();

  useEffect(() => {
    if (apiLineage && apiDagDepthBoundary) {
      setDagDepthBoundary(apiDagDepthBoundary);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiDagDepthBoundary]);

  const curReactFlowGraph = useMemo(
    () => ({ nodes: graphNodes || [], edges }),
    [edges, graphNodes],
  );

  const initWorker = useCallback(() => {
    lineageDataWorker = new LineageDataWorker();

    lineageDataWorker.onmessage = async (
      message: MessageEvent<PostMessageData>,
    ) => {
      const { data, type } = message.data;
      if (type === 'getGraph') {
        setRootNodes(data.rootNodes);
        setNodes(data.reactFlowGraph.nodes);
        setEdges(data.reactFlowGraph.edges);
        setUpstreamNodes(data.upstreamNodes);
        setDownstreamNodes(data.downstreamNodes);
        if (!isApiEnabled) {
          setDagDepthBoundary(data.dagDepthBoundary);
        }
      } else if (
        type === 'highlightNode' ||
        type === 'toggleExpandNode' ||
        type === 'setHighlightColumnSubgraph'
      ) {
        const { reactFlowGraph } = data;
        setNodes(reactFlowGraph.nodes);
        setEdges(reactFlowGraph.edges);
      } else if (type === 'addAssetColumns') {
        const { reactFlowGraph, assetId } = data;
        setNodes(reactFlowGraph.nodes);
        setEdges(reactFlowGraph.edges);
        setColumnsLoadingAssetIds((prev) => withoutValue(prev, assetId));
      } else if (type === 'largeGraphWarning') {
        setHitGraphLimits(true);
        setNodes([]);
        setEdges([]);
        setLoading(false);
        return;
      } else if (type === 'error') {
        setWorkerError(data ?? { message: 'Unknown worker error' });
        return;
      }

      setHitGraphLimits(false);
      setWorkerError(null);
      setLoading(false);
    };
  }, [isApiEnabled]);

  const getGraph = useCallback(
    (
      highlightNodeId?: string,
      expandNodes?: string[],
      collapseNodes?: string[],
      lockPosNodes?: string[],
      highlightSubgraph?: ApiLineage,
    ) => {
      if (!selectedNodes?.length) {
        setNodes([]);
        setEdges([]);
        setHitGraphLimits(false);
        setLoading(false);
        return;
      }

      if (selectionTooLarge) {
        setHitGraphLimits(true);
        setNodes([]);
        setEdges([]);
        setLoading(false);
        return;
      }

      setHitGraphLimits(false);
      setLoading(true);

      if (
        overrideLineageData == null &&
        isApiEnabled &&
        (isLineageFetching || !apiLineage)
      ) {
        return;
      }

      if (overrideLineageData != null && !apiLineage) {
        return;
      }

      if (!lineageDataWorker) {
        initWorker();
      }

      const messageData: OnMessageData = {
        type: 'getGraph',
        data: {
          reportData,
          selectedNodes,
          filters,
          streamDirection,
          hopLimit,
          reactFlowGraph: curReactFlowGraph,
          highlightedNode: highlightNodeId,
          expandNodes,
          collapseNodes,
          lockPosNodes,
          calcDownstreamUpstream,
          excludeColumnNodes,
          highlightColumnSubgraph: highlightSubgraph,
          apiLineage: apiLineage || undefined,
          forceLoadLargeGraph,
        },
      };
      lineageDataWorker?.postMessage(messageData);
    },
    [
      selectedNodes,
      selectionTooLarge,
      isApiEnabled,
      isLineageFetching,
      apiLineage,
      reportData,
      filters,
      streamDirection,
      hopLimit,
      curReactFlowGraph,
      calcDownstreamUpstream,
      forceLoadLargeGraph,
      initWorker,
      overrideLineageData,
      excludeColumnNodes,
    ],
  );

  const onToggleExpandNode = useCallback(
    async (node: ReactFlowNode<ReactFlowNodeData>) => {
      const children = getReactFlowNodeChildrenRecursively(node);
      if (children?.some((n) => n.id === highlightedNode?.id)) {
        setHighlightedNode(undefined);
        getGraph(undefined, undefined, [node.id], [node.id]);
        return;
      }

      const shouldExpand = !node.data.isExpanded;
      const needsColumnFetch =
        isApiEnabled &&
        !hasColumnFilter &&
        shouldExpand &&
        !hasLoadedColumnChildren(node) &&
        (node.data.columnsCount ?? 0) > 0;

      if (needsColumnFetch) {
        if (!lineageDataWorker) {
          initWorker();
        }
        // lineageDataWorker can be recreated while we await fetchAssetColumns;
        // capture the current one to detect that below.
        const workerAtRequest = lineageDataWorker;

        setColumnsLoadingAssetIds((prev) => new Set(prev).add(node.id));

        const clearLoading = () =>
          setColumnsLoadingAssetIds((prev) => withoutValue(prev, node.id));

        const currentlyExpandedNodes = nodes
          .filter((n) => n.data.isExpanded)
          .map((n) => n.id);

        try {
          const columns = await fetchAssetColumns(node.id);

          // Worker swapped out mid-fetch — columns belong to a stale graph.
          if (lineageDataWorker !== workerAtRequest) {
            clearLoading();
            return;
          }

          const messageData: OnMessageData = {
            type: 'addAssetColumns',
            data: {
              assetId: node.id,
              columns,
              selectedNodes,
              highlightedNode: highlightedNode?.id,
              reactFlowGraph: curReactFlowGraph,
              currentlyExpandedNodes,
            },
          };
          lineageDataWorker?.postMessage(messageData);
        } catch {
          clearLoading();
        }
        return;
      }

      const messageData: OnMessageData = {
        type: 'toggleExpandNode',
        data: {
          node,
          selectedNodes,
          highlightedNode: highlightedNode?.id,
          reactFlowGraph: curReactFlowGraph,
        },
      };

      lineageDataWorker?.postMessage(messageData);
    },
    [
      curReactFlowGraph,
      fetchAssetColumns,
      getGraph,
      hasColumnFilter,
      highlightedNode,
      initWorker,
      isApiEnabled,
      nodes,
      selectedNodes,
      setHighlightedNode,
    ],
  );

  const onToggleHighlightNode = useCallback(
    (node?: ReactFlowNode<ReactFlowNodeData>) => {
      const prevHighlightedNode = highlightedNode
        ? nodes.find((n) => n.id === highlightedNode.id)
        : undefined;

      const nodeToSet =
        !node || node?.id === highlightedNode?.id
          ? undefined
          : {
              id: node.id,
              parentId: node?.data.metadata.parent,
              metadata: node?.data.metadata,
            };

      if (!highlightedNode && !nodeToSet) return;

      setHighlightedNode(nodeToSet);

      const isChild = !!node?.data?.parentNode;

      if (!isChild && !prevHighlightedNode?.data.parentNode) {
        const messageData: OnMessageData = {
          type: 'highlightNode',
          data: { nodeId: nodeToSet?.id, reactFlowGraph: curReactFlowGraph },
        };
        lineageDataWorker?.postMessage(messageData);
        return;
      }

      const prevHighlightedNodeParents =
        prevHighlightedNode && getReactFlowNodeParentIds(prevHighlightedNode);

      const nodeToSetParents = node && getReactFlowNodeParentIds(node);

      const keepExpanded = prevHighlightedNodeParents;

      const lockPos =
        nodeToSetParents || [nodeToSet?.id] ||
        (prevHighlightedNodeParents && !nodeToSet)
          ? prevHighlightedNodeParents
          : undefined;

      const isColumnChild = isChild && isColumnNode(node?.data.metadata);

      const shouldUseColumnHighlightSubgraph =
        isApiEnabled && !hasColumnFilter && isColumnChild && !!nodeToSet;

      if (shouldUseColumnHighlightSubgraph) {
        pendingHighlightSubgraphRef.current = nodeToSet?.id;
        return;
      }

      pendingHighlightSubgraphRef.current = undefined;
      getGraph(nodeToSet?.id, keepExpanded, undefined, lockPos);
    },
    [
      curReactFlowGraph,
      getGraph,
      hasColumnFilter,
      highlightedNode,
      isApiEnabled,
      nodes,
      setHighlightedNode,
    ],
  );

  useEffect(() => {
    const isColumnHighlightActive =
      isApiEnabled &&
      !hasColumnFilter &&
      overrideLineageData == null &&
      !!highlightedNode?.parentId &&
      isColumnNode(highlightedNode.metadata);

    if (!isColumnHighlightActive) {
      pendingHighlightSubgraphRef.current = undefined;
      return;
    }

    if (
      pendingHighlightSubgraphRef.current !== highlightedNode.id ||
      isHighlightLineageFetching ||
      !highlightColumnSubgraph
    ) {
      return;
    }

    const highlightedReactFlowNode = nodes.find(
      (node) => node.id === highlightedNode.id,
    );
    const lockPos =
      highlightedReactFlowNode &&
      getReactFlowNodeParentIds(highlightedReactFlowNode);

    if (!lineageDataWorker) {
      initWorker();
    }

    const messageData: OnMessageData = {
      type: 'setHighlightColumnSubgraph',
      data: {
        highlightColumnSubgraph,
        selectedNodes,
        highlightedNode: highlightedNode.id,
        reactFlowGraph: curReactFlowGraph,
        expandNodes: lockPos,
        lockPosNodes: lockPos,
      },
    };
    lineageDataWorker?.postMessage(messageData);
    pendingHighlightSubgraphRef.current = undefined;
  }, [
    curReactFlowGraph,
    hasColumnFilter,
    highlightColumnSubgraph,
    highlightedNode,
    initWorker,
    isApiEnabled,
    isHighlightLineageFetching,
    nodes,
    overrideLineageData,
    selectedNodes,
  ]);

  const filteredNodes = useMemo(() => {
    const nodeExpansionMap = new Map();
    nodes.forEach((node) => {
      nodeExpansionMap.set(node.id, node.data.isExpanded);
    });

    const nodesWithoutChildrenOfCollapsedParents = nodes.filter((n) => {
      if (!n.parentNode) {
        return true;
      }

      const parentIds = getReactFlowNodeParentIds(n);

      return parentIds.every((parentId) => nodeExpansionMap.get(parentId));
    });

    return nodesWithoutChildrenOfCollapsedParents.map((node) => {
      if (!columnsLoadingAssetIds.has(node.id)) {
        return node;
      }

      return {
        ...node,
        data: {
          ...node.data,
          isColumnsLoading: true,
        },
      };
    });
  }, [columnsLoadingAssetIds, nodes]);

  const stringifiedGetGraphDeps = JSON.stringify([
    selectedNodes,
    filters,
    streamDirection,
    hopLimit,
    isApiEnabled || overrideLineageData != null ? apiLineage : null,
    forceLoadLargeGraph,
    overrideLineageData != null,
  ]);

  const terminateWorker = useCallback(() => {
    lineageDataWorker?.terminate();
    lineageDataWorker = null;
  }, []);

  useEffect(() => {
    terminateWorker();
    getGraph();
    setHighlightedNode(undefined);
    setColumnsLoadingAssetIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringifiedGetGraphDeps]);

  useEffect(() => {
    terminateWorker();
    getGraph();
    setColumnsLoadingAssetIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData]);

  useEffect(
    () => () => {
      terminateWorker();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (workerError) {
      const error = new Error(`Lineage worker error: ${workerError.message}`);
      if (workerError.stack) {
        error.stack = workerError.stack;
      }
      throw error;
    }
  }, [workerError]);

  return {
    rootNodes,
    nodes: filteredNodes,
    edges,
    onToggleExpandNode,
    onToggleHighlightNode,
    upstreamNodes,
    downstreamNodes,
    dagDepthBoundary,
    loading: loading || isHighlightLineageFetching,
    hitGraphLimits,
    exceededLimitParentAssets,
  };
};

export default useLineageData;
